import React, { memo, useEffect, useRef, useState } from 'react';
import { Platform, StyleSheet, TextInput, TouchableOpacity, View } from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import { useI18n } from '../i18n';

// Search field on the Events feed (between "Featured This Week" and "All
// events"). Keeps its own text so typing never re-renders the feed; the feed
// gets the query after a short pause.
const SEARCH_DEBOUNCE_MS = 250;

type Props = {
  onSearch: (query: string) => void;
  testID?: string;
};

const FeedSearchBar: React.FC<Props> = ({ onSearch, testID }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const [text, setText] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onSearchRef = useRef(onSearch);
  onSearchRef.current = onSearch;

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
  }, []);

  const update = (next: string) => {
    setText(next);
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!next.trim()) {
      onSearchRef.current('');
      return;
    }
    timerRef.current = setTimeout(() => onSearchRef.current(next), SEARCH_DEBOUNCE_MS);
  };

  const surface = theme.isDark ? '#111826' : theme.colors.surfaceElevated;
  const label = t('feed.searchPlaceholder');
  const accessibilityLabel = t('feed.searchAccessibility', 'Search events, clubs or tags');
  return (
    <View style={[styles.box, { backgroundColor: surface, borderColor: theme.colors.border }]} testID={testID}>
      <Icon name="search" size={16} color={theme.colors.iconSubtle} />
      <TextInput
        value={text}
        onChangeText={update}
        onSubmitEditing={() => onSearchRef.current(text)}
        placeholder={label}
        placeholderTextColor={theme.colors.textMuted}
        style={[styles.input, { color: theme.colors.textPrimary }]}
        returnKeyType="search"
        autoCorrect={false}
        autoCapitalize="none"
        accessibilityLabel={accessibilityLabel}
        testID={testID ? `${testID}-input` : undefined}
      />
      {text ? (
        <TouchableOpacity
          style={styles.clear}
          onPress={() => update('')}
          accessibilityRole="button"
          accessibilityLabel={t('common.clear')}
          hitSlop={6}
        >
          <Icon name="x" size={15} color={theme.colors.iconMuted} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
};

const styles = StyleSheet.create({
  box: {
    height: 44,
    marginTop: 4,
    marginBottom: 6,
    borderRadius: 14,
    borderWidth: 1,
    paddingLeft: 13,
    paddingRight: 8,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
  },
  input: {
    flex: 1,
    minWidth: 0,
    height: '100%',
    paddingVertical: 0,
    fontSize: 14,
    fontWeight: Typography.semibold,
    ...Platform.select({ web: { outlineStyle: 'none' } as object, default: {} }),
  },
  clear: { width: 30, height: 30, alignItems: 'center', justifyContent: 'center' },
});

export default memo(FeedSearchBar);
